import React, { useState, useEffect } from 'react';
import { TrendingUp, Users, Layers, Bot, MessageCircle } from 'lucide-react';
import SymbolsTab from './tabs/SymbolsTab';
import AnalystFilterTab from './tabs/AnalystFilterTab';
import PatternsTab from './tabs/PatternsTab';
import AICopilotTab from './tabs/AICopilotTab';
import HermesAgentTab from './tabs/HermesAgentTab';
import { publishSidebar, registerSidebarSetters } from '../../stores/chartSidebarStore';
import { AGENT_NAME } from '../../config';

const TABS = [
  { id: 'symbols',  icon: TrendingUp,    label: 'Symbols' },
  // plan 181: Analysts promoted to its own tab (was nested in Symbols) — the
  // dedicated controlled view + the source of the available-analysts list.
  { id: 'analysts', icon: Users,         label: 'Analysts' },
  { id: 'patterns', icon: Layers,        label: 'Patterns' },
  { id: 'copilot',  icon: Bot,           label: 'AI Copilot' },
  // id stays 'hermes' (internal tab key); only the visible label is the brand.
  { id: 'hermes',   icon: MessageCircle, label: AGENT_NAME },
];

export default function ChartControlPanel({
  overlayState,
  onOverlayChange,
  onSelectSymbol,
  currentSymbol,
  chartRef,
}) {
  const [activeTab, setActiveTab] = useState('symbols');
  const [panelOpen, setPanelOpen] = useState(true);

  // plan 181: mirror the active tab + register setActiveTab so the CLI
  // (window.__brochart selectTab) can switch tabs and open the panel.
  useEffect(() => { publishSidebar({ activeTab }); }, [activeTab]);
  useEffect(() => {
    registerSidebarSetters({
      setActiveTab: (id) => { if (id) { setActiveTab(id); setPanelOpen(true); } },
    });
  }, []);

  const handleTabClick = (id) => {
    if (activeTab === id && panelOpen) {
      setPanelOpen(false);
    } else {
      setActiveTab(id);
      setPanelOpen(true);
    }
  };

  const renderPanel = () => {
    switch (activeTab) {
      case 'symbols':
        return (
          <SymbolsTab
            onSelectSymbol={onSelectSymbol}
            overlayState={overlayState}
            onOverlayChange={onOverlayChange}
          />
        );
      case 'analysts':
        return (
          <AnalystFilterTab
            currentSymbol={currentSymbol}
            state={overlayState}
            onChange={onOverlayChange}
          />
        );
      case 'patterns':
        return <PatternsTab currentSymbol={currentSymbol} chartRef={chartRef} />;
      case 'copilot':
        return <AICopilotTab />;
      case 'hermes':
        return <HermesAgentTab currentSymbol={currentSymbol} />;
      default:
        return null;
    }
  };

  return (
    <div className="flex h-full flex-shrink-0">
      {/* Sliding panel */}
      {panelOpen && (
        <div className="w-64 h-full flex flex-col bg-[#1e2030] border-l border-gray-700/60 overflow-hidden">
          {/* Panel header */}
          <div className="flex items-center justify-between px-3 py-2.5 border-b border-gray-700/60 flex-shrink-0">
            <span className="text-xs font-semibold text-gray-300 uppercase tracking-wider">
              {TABS.find(t => t.id === activeTab)?.label}
            </span>
            <button
              onClick={() => setPanelOpen(false)}
              className="w-5 h-5 rounded flex items-center justify-center text-gray-500 hover:text-gray-300 hover:bg-gray-700 transition-colors"
            >
              <svg viewBox="0 0 24 24" className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth={2.5}>
                <path strokeLinecap="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Panel content */}
          <div className="flex-1 overflow-y-auto overflow-x-hidden">
            {renderPanel()}
          </div>
        </div>
      )}

      {/* Icon strip */}
      <div className="w-10 flex-shrink-0 flex flex-col items-center pt-2 bg-[#1a1c2a] border-l border-gray-700/60 gap-0.5">
        {TABS.map(({ id, icon: Icon, label }) => {
          const isActive = activeTab === id && panelOpen;
          return (
            <button
              key={id}
              title={label}
              onClick={() => handleTabClick(id)}
              className={`w-8 h-8 rounded flex items-center justify-center transition-colors ${
                isActive
                  ? 'bg-blue-600 text-white'
                  : 'text-gray-500 hover:text-gray-200 hover:bg-gray-700/60'
              }`}
            >
              <Icon size={16} strokeWidth={isActive ? 2.5 : 2} />
            </button>
          );
        })}
      </div>
    </div>
  );
}
